import { createClient } from '@supabase/supabase-js';

/**
 * DEFENSIVE ENVIRONMENT ACCESS
 * In some preview environments, import.meta.env may be undefined.
 * This helper prevents a top-level crash.
 */
const safeGetEnv = (key: string): string => {
  try {
    // Attempt Vite-style static replacement
    if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
      return (import.meta as any).env[key] || '';
    }
  } catch (e) {}

  try {
    // Attempt Node-style process.env fallback
    if (typeof process !== 'undefined' && process.env) {
      return process.env[key] || '';
    }
  } catch (e) {}

  return '';
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Verify configuration status
const isConfigured =
  supabaseUrl.length > 10 && supabaseAnonKey.length > 40 && supabaseUrl.startsWith('https://');

export const isSupabaseConfigured = () => isConfigured;
export const getSupabaseUrl = () => supabaseUrl || 'NOT_CONFIGURED';

// We initialize even if not configured to prevent null-pointer errors in components.
// The AuthScreen handles the "Not Configured" UI state.
export const supabase = createClient(
  isConfigured ? supabaseUrl : 'https://placeholder-project.supabase.co',
  isConfigured ? supabaseAnonKey : 'placeholder-anon-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storageKey: 'scoutloop_auth_v7',
      storage: window.localStorage,
    },
  }
);

export const hardClearSession = () => {
  const keysToClear = ['scoutloop_auth_v7', 'sl_pipeline'];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.includes('supabase') || key.includes('sb-'))) {
      localStorage.removeItem(key);
    }
  }
  keysToClear.forEach((key) => window.localStorage.removeItem(key));
  window.sessionStorage.clear();
};

export const nuclearReset = () => {
  window.localStorage.clear();
  window.sessionStorage.clear();
  window.location.replace(window.location.origin);
};
